/**
 * StewardCard — one grounded recommendation, with the Add button that makes it
 * real.
 *
 * Two honest disciplines are visible in the markup:
 *  1. A rating or price renders ONLY when the server attached a source. There is
 *     no "price unavailable" placeholder — an absent number is simply absent,
 *     because inventing one is the failure mode this whole feature guards against.
 *  2. Adding is the USER's action. The card calls the existing
 *     itineraryApi.createItem — the same write path as the manual add dialog —
 *     then invalidates the itinerary queries so the day ribbon updates. Steward
 *     proposed; the tap executed.
 *
 * Photos load through the image proxy (SSRF-guarded, 2MB cap), never hotlinked,
 * and a missing/broken photo degrades to a clean typographic header rather than
 * a broken-image box.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ExternalLink,
  Loader2,
  MapPin,
  Plus,
  Star,
  ChevronDown,
  Search,
} from "lucide-react";
import { itineraryApi, stewardApi } from "@/lib/api";
import type { StewardCardData } from "@/lib/types";
import {
  CATEGORY_LABEL,
  cardSubtitle,
  formatPrice,
  formatRateDates,
  formatRating,
  prefillToItemInput,
  priceSourceLabel,
  ratesUnavailable,
} from "./stewardCard-utils";
import { invalidateAfterItineraryWrite } from "@/lib/itineraryCache";

export type AddState = "idle" | "adding" | "added";

interface ViewProps {
  card: StewardCardData;
  addState: AddState;
  onAdd: () => void;
  error?: string | null;
  /** Server-rendered map embed state (the toggle is client-only). */
  mapOpen?: boolean;
  onToggleMap?: () => void;
}

/**
 * Presentational card. Split from the container so both the idle and added
 * states can be rendered and asserted without a DOM.
 */
export function StewardCardView({
  card,
  addState,
  onAdd,
  error,
  mapOpen = false,
  onToggleMap,
}: ViewProps) {
  const rating = formatRating(card);
  const price = formatPrice(card);
  const rateDates = formatRateDates(card);
  const rateSource = priceSourceLabel(card);
  const noRate = ratesUnavailable(card);
  const added = addState === "added";

  return (
    <article className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {card.image_url && (
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
          <img
            src={stewardApi.imageUrl(card.image_url)}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
            onError={(e) => {
              // A dead photo must not leave a broken-image box behind.
              (e.currentTarget.parentElement as HTMLElement).style.display = "none";
            }}
          />
        </div>
      )}

      <div className="space-y-3 p-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {CATEGORY_LABEL[card.type] ?? "Do"}
            </p>
            <h4 className="truncate text-sm font-semibold text-foreground">{card.title}</h4>
            <p className="truncate text-xs text-muted-foreground">{cardSubtitle(card)}</p>
          </div>

          {/* Sourced facts only. No source, no number, no placeholder. */}
          <div className="flex shrink-0 flex-col items-end gap-1">
            {rating && (
              <a
                href={card.rating!.source}
                target="_blank"
                rel="noopener noreferrer"
                title="Rating from the venue's own page"
                className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted/70"
              >
                <Star className="h-3 w-3 fill-current text-amber-500" aria-hidden="true" />
                <span className="font-mono">{rating}</span>
              </a>
            )}
            {/* A rate is only a fact WITH its nights and its source, so the
                three render together or not at all. */}
            {price && (
              <a
                href={card.price!.source}
                target="_blank"
                rel="noopener noreferrer"
                title={
                  rateDates
                    ? `Rate for ${rateDates}${rateSource ? `, via ${rateSource}` : ""}`
                    : "Price from the venue's own page"
                }
                className="flex flex-col items-end gap-0.5 text-right hover:underline"
              >
                <span className="font-mono text-[11px] font-semibold text-foreground">{price}</span>
                {rateDates && (
                  <span className="text-[10px] font-normal text-muted-foreground">
                    <span className="font-mono">{rateDates}</span>
                    {rateSource ? ` · ${rateSource}` : ""}
                  </span>
                )}
              </a>
            )}

            {/* The honest empty state. A blank here would read as "free". The
                traveler's next move is the search link below, so this says what
                happened and stops — it does not repeat the call to action. */}
            {!price && noRate && (
              <span className="text-right text-[10px] leading-tight text-muted-foreground">
                No price for
                <br />
                {card.type === "car" ? "those dates" : "those nights"}
              </span>
            )}
          </div>
        </div>

        {card.highlights.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {card.highlights.slice(0, 4).map((h) => (
              <li
                key={h}
                className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
              >
                {h}
              </li>
            ))}
          </ul>
        )}

        {/* Keyless map affordances — a search link plus an optional embed. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
          <a
            href={stewardApi.mapsLink(card.map_query)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> Open in Maps
          </a>
          {onToggleMap && (
            <button
              type="button"
              onClick={onToggleMap}
              aria-expanded={mapOpen}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <ChevronDown
                className={"h-3.5 w-3.5 transition-transform motion-reduce:transition-none " + (mapOpen ? "rotate-180" : "")}
                aria-hidden="true"
              />
              {mapOpen ? "Hide map" : "Show map"}
            </button>
          )}
          {card.url && (
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Website
            </a>
          )}
        </div>

        {/* The dated search. It is the answer to "so what does it cost?" for
            everything we cannot price — a car, a train, a tour, a table — and
            it is honest about being a search rather than a quote. Server-built
            from the trip's real dates, so the model can't point it anywhere. */}
        {card.booking_link && (
          <a
            href={card.booking_link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/40 hover:text-foreground motion-reduce:transition-none"
          >
            <Search className="h-3.5 w-3.5" aria-hidden="true" />
            {card.booking_link.label}
          </a>
        )}

        {mapOpen && (
          <iframe
            title={`Map of ${card.title}`}
            src={stewardApi.mapsEmbed(card.map_query)}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="h-44 w-full rounded-lg border border-border"
          />
        )}

        <div className="flex items-center gap-2 pt-0.5">
          <button
            type="button"
            onClick={onAdd}
            disabled={added || addState === "adding"}
            aria-label={added ? `${card.title} added to itinerary` : `Add ${card.title} to itinerary`}
            className={
              "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors motion-reduce:transition-none " +
              (added
                ? "cursor-default bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200"
                : "bg-[hsl(var(--foreground))] text-white hover:opacity-90 disabled:opacity-60")
            }
          >
            {addState === "adding" ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                Adding
              </>
            ) : added ? (
              <>
                <Check className="h-3.5 w-3.5" aria-hidden="true" /> Added
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add to itinerary
              </>
            )}
          </button>
          {error && <span className="text-xs text-destructive">{error}</span>}
        </div>
      </div>
    </article>
  );
}

interface Props {
  card: StewardCardData;
  tripId: string;
}

/** Container: owns the add mutation + cache invalidation. */
export default function StewardCard({ card, tripId }: Props) {
  const queryClient = useQueryClient();
  const [mapOpen, setMapOpen] = useState(false);

  const add = useMutation({
    mutationFn: () => itineraryApi.createItem(tripId, prefillToItemInput(card.itinerary_prefill)),
    onSuccess: () => {
      // The day ribbon and the unified plan both derive from these.
      invalidateAfterItineraryWrite(queryClient, tripId);
    },
  });

  const addState: AddState = add.isSuccess ? "added" : add.isPending ? "adding" : "idle";

  return (
    <StewardCardView
      card={card}
      addState={addState}
      onAdd={() => {
        // Idempotent at the UI level: once added, the button is inert.
        if (!add.isSuccess && !add.isPending) add.mutate();
      }}
      error={add.isError ? "Couldn't add that — try again." : null}
      mapOpen={mapOpen}
      onToggleMap={() => setMapOpen((v) => !v)}
    />
  );
}
